import React from "react";
import styled from "styled-components";
import { Info, Lock } from "lucide-react";
import { PricingBreakdown } from "../../../../Data/imagePurchase.dto";

interface PricingBreakdownCardProps {
  breakdown: PricingBreakdown | null;
  loading: boolean;
  error: string;
}

export const formatINR = (value: number | null | undefined) =>
  `₹${Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const PricingBreakdownCard: React.FC<PricingBreakdownCardProps> = ({
  breakdown,
  loading,
  error,
}) => {
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (!breakdown) return loading ? <Muted>Loading charges…</Muted> : null;

  if (breakdown.pricing_policy === "disabled") {
    return (
      <DisabledBox>
        <Lock size={16} />
        <span>{breakdown.policy_label}</span>
      </DisabledBox>
    );
  }

  const { per_image, commission, service_fee, examples, notes } = breakdown;
  const platformPaysFee = service_fee.payer === "platform";

  return (
    <Card $dim={loading}>
      <PolicyBadge>{breakdown.policy_label}</PolicyBadge>

      {per_image && (
        <Rows>
          <Row>
            <span>Customer pays per image</span>
            <strong>{formatINR(per_image.customer_pays)}</strong>
          </Row>
          {commission.enabled && (
            <Row $negative>
              <span>Platform commission ({commission.percent}%)</span>
              <strong>− {formatINR(per_image.commission_deducted)}</strong>
            </Row>
          )}
          <Row>
            <span>Payment gateway fee ({service_fee.percent}%)</span>
            <strong>
              {platformPaysFee
                ? "Paid by platform"
                : `+ ${formatINR(per_image.service_fee_charged_to_customer)} to customer`}
            </strong>
          </Row>
          <Row $total>
            <span>You receive per image</span>
            <strong>{formatINR(per_image.studio_receives)}</strong>
          </Row>
        </Rows>
      )}

      {examples.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Images</th>
              <th>Customer pays</th>
              {commission.enabled && <th>Commission</th>}
              <th>You receive</th>
            </tr>
          </thead>
          <tbody>
            {examples.map((ex) => (
              <tr key={ex.images}>
                <td>{ex.images}</td>
                <td>{formatINR(ex.customer_pays)}</td>
                {commission.enabled && <td>{formatINR(ex.commission_deducted)}</td>}
                <td>{formatINR(ex.studio_receives)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {notes.length > 0 && (
        <Notes>
          {notes.map((note) => (
            <li key={note}>
              <Info size={12} />
              <span>{note}</span>
            </li>
          ))}
        </Notes>
      )}
    </Card>
  );
};

export default PricingBreakdownCard;

const Card = styled.div<{ $dim?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(201, 77, 170, 0.14);
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.06) 0%,
    rgba(206, 86, 176, 0.05) 100%
  );
  opacity: ${({ $dim }) => ($dim ? 0.6 : 1)};
  transition: opacity 0.15s ease;
`;

const PolicyBadge = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Row = styled.div<{ $negative?: boolean; $total?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.65);

  strong {
    font-family: Urbanist, sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: ${({ $negative }) => ($negative ? "#e44862" : "#171717")};
    text-align: right;
  }

  ${({ $total }) =>
    $total &&
    `
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px dashed rgba(23, 23, 23, 0.12);
    color: #171717;
    font-weight: 700;
    strong { color: #31a66a; font-size: 15px; }
  `}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Montserrat, sans-serif;
  font-size: 11px;

  th,
  td {
    padding: 6px 4px;
    text-align: right;
    border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  th {
    font-weight: 700;
    color: rgba(23, 23, 23, 0.45);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 10px;
  }

  td {
    font-weight: 600;
    color: #171717;
  }
`;

const Notes = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;

  li {
    display: flex;
    gap: 6px;
    align-items: flex-start;
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.45;
    color: rgba(23, 23, 23, 0.55);
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Muted = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
`;

const ErrorBox = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #e44862;
`;

const DisabledBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(228, 72, 98, 0.06);
  border: 1px solid rgba(228, 72, 98, 0.2);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #e44862;
`;
